import { useListFilters, useListPagination } from "../../hooks/useListState.js";
import { useTabState } from "../../hooks/useTabState.js";
import { STUDENT_TABS } from "../../lib/tabState.js";
import { screenDetailScope } from "../../lib/screenState.js";
import { validateListFilters } from "../../lib/listState.js";
import { isManager, rpc } from "../../lib/supabase.js";
import { paymentDetail } from "../payments/paymentApi.js";
import { FileDownload } from "../../components/FileDownload.jsx";
import { RouteTargetNotice } from "../../components/RouteTargetNotice.jsx";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { ArrowLeft, FileText, GraduationCap, Pencil, Plus, RotateCcw, Search } from "lucide-react";
import { App, Alert, Button, Card, Col, DatePicker, Descriptions, Divider, Flex, Form, Input, InputNumber, Modal as AntModal, Radio, Row, Segmented, Select, Space, Table, Tabs, Tag, Tooltip, Typography, Upload } from "antd";
import koKR from "antd/locale/ko_KR";
import { Panel } from "../../components/Panel.jsx";
import { AppEmpty, ContentLoading, StatusTag } from "../../components/Feedback.jsx";
import { matchesSchoolSearch } from "../../data/schools.js";
import { readLimitedFiles } from "../../utils/files.js";
import { academyDateKey, formatDate, todayKey } from "../../utils/date.js";
import { CURRENT_YEAR, formatWon, getCourseLabel, getCurrentMonthKey, getEnrollmentStatus, getStudentGradeLabel, getStudentPromotion } from "../../utils/academy.js";
import { ScoreEditor, StudentEditor } from "./StudentForms.jsx";
import { ConsultationTable, ScoreTable } from "./StudentTables.jsx";
import { formatStudentContact } from "../../utils/studentContact.js";
import { studentSchoolName, studentSchoolOptions as getStudentSchoolOptions } from "../../utils/studentSchool.js";
import { CourseSetupModal } from "./CourseSetupModal.jsx";
import { StudentActions } from "./StudentActions.jsx";
import { compareStudentListOrder } from "./studentListOrder.js";

import { enrollmentCourseLabel, enrollmentCourseTerms, enrollmentTermKey } from "./enrollmentCourseOptions.js";

import { studentListSchoolName, studentListGrade } from "./studentListLabels.js";

const blankStudent = { name: "", gender: "", schoolLevel: "", school: "", grade: "", phone: "", parentPhone: "", parentName: "", guardianRelationship: "", billingMethod: "매번 선택", joinedAt: academyDateKey(), status: "재원", memo: "", schoolConfirmationRequired: false, graduationReviewRequired: false };
const blankScore = { title: "", subject: "", date: todayKey(), score: "", maxScore: 100, level: "중", comment: "" };
const blankEnrollment = (startDate = todayKey()) => ({ courseGroupId: "", startDate, endDate: "", endReason: "", endReasonNote: "", appliedTuition: "", firstMonthAdjustment: "", memo: "" });
const ScoreChart = lazy(() => import("../../components/ScoreChart.jsx"));
const courseTermOrder = { semester_1: 1, summer: 2, semester_2: 3, winter: 4 };
const defaultStudentFilter = { name: "", school: "", grade: "", status: "", courseType: "all" };
const studentStatusValues = ["재원", "휴원", "퇴원", "졸업"];

function clickableRow(onClick) {
  return (record) => ({ onClick: () => onClick(record.id), style: { cursor: "pointer" } });
}

function renderStudentStatusTag(status) {
  const tone = status === "재원" ? "positive" : status === "휴원" ? "neutral" : status === "퇴원" ? "danger" : "info";
  return <StatusTag tone={tone}>{status || "-"}</StatusTag>;
}

function renderStudentReviewTag(student) {
  if (student.schoolConfirmationRequired) return <StatusTag tone="warning">학교 확인 필요</StatusTag>;
  if (student.graduationReviewRequired) return <StatusTag tone="warning">졸업 확인 필요</StatusTag>;
  return null;
}

function renderEnrollmentStatusTag(status) {
  const tone = status === "수강 중" ? "positive" : status === "예정" ? "info" : "neutral";
  return <StatusTag tone={tone}>{status}</StatusTag>;
}

function compareRecentCourses(firstCourse, secondCourse) {
  const yearDifference = Number(secondCourse.year || 0) - Number(firstCourse.year || 0);
  if (yearDifference) return yearDifference;
  const firstTerm = firstCourse.term || firstCourse.semester || "semester_1";
  const secondTerm = secondCourse.term || secondCourse.semester || "semester_1";
  return (courseTermOrder[secondTerm] || 0) - (courseTermOrder[firstTerm] || 0);
}

function newConsultationForm(studentId = "") {
  return { studentId, date: dayjs().format("YYYY-MM-DDTHH:mm"), type: "학생상담", content: "", files: [] };
}

function getScheduledChangeSource(change) {
  if (change.source) return change.source;
  if (change.result === "promotion") return Object.hasOwn(change, "school") ? "schoolConfirmation" : "regular";
  return "graduation";
}

export function StudentsPage({ store, setPageTitleOverride, navigation }) {
  const manager = isManager(store.context.role);
  const { message, modal } = App.useApp();
  const { state, selectedStudentId, setSelectedStudentId, update, createId } = store;
  const routeStudentId = navigation?.route.kind === "student" ? navigation.route.id : "";
  const [pageMode, setPageMode] = useState(routeStudentId ? "detail" : "list");
  const requestedStudent = useRef("");
  useEffect(() => {
    if (!routeStudentId || store.loading || requestedStudent.current === routeStudentId) return;
    requestedStudent.current = routeStudentId;
    if (state.students.some(item => item.id === routeStudentId)) setSelectedStudentId(routeStudentId);
  }, [routeStudentId, store.loading, state.students, setSelectedStudentId]);
  const validateStudentFilters = (v) => validateListFilters(v, defaultStudentFilter, {
    status: studentStatusValues,
    courseType: manager ? ["all", "regular_only", "special_only", "regular_and_special"] : ["all"],
    school: (store.loading || !!store.error) ? null : [...getStudentSchoolOptions(state.academyInfo).map(o => o.value), ...state.students.map(s => s.school)],
    grade: (store.loading || !!store.error) ? null : state.students.map(s => getStudentGradeLabel(s)),
  });
  const [studentFilterInput, setStudentFilterInput] = useListFilters("list:students:input", defaultStudentFilter, validateStudentFilters);
  const [studentFilter, setStudentFilter] = useListFilters("list:students:filter", defaultStudentFilter, validateStudentFilters);
  const [form, setForm] = useState(blankStudent);
  const [editingId, setEditingId] = useState("");
  const registrationId = useRef(null);
  const [scoreForm, setScoreForm] = useState(blankScore);
  const [scoreFileList, setScoreFileList] = useState([]);
  const [consultationForm, setConsultationForm] = useState(newConsultationForm());
  const [consultationFileList, setConsultationFileList] = useState([]);
  const [consultationEditForm, setConsultationEditForm] = useState(null);
  const [consultationEditFileList, setConsultationEditFileList] = useState([]);
  const [activeTab, setActiveTab] = useTabState(screenDetailScope(navigation?.route || {}) || "tab:student", "chart", STUDENT_TABS);
  const [scoreModalOpen, setScoreModalOpen] = useState(false);
  const [selectedScore, setSelectedScore] = useState(null);
  const [enrollmentSaveError, setEnrollmentSaveError] = useState("");
  const [enrollmentSavedWarning, setEnrollmentSavedWarning] = useState("");
  const enrollmentFeedbackRef = useRef(null);
  useEffect(() => {
    if (!enrollmentSaveError && !enrollmentSavedWarning) return;
    const node = enrollmentFeedbackRef.current;
    if (node) { node.closest(".ant-modal-body")?.scrollTo({ top: 0 }); node.focus({ preventScroll: true }); }
  }, [enrollmentSaveError, enrollmentSavedWarning]);
  const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
  const [editingEnrollmentId, setEditingEnrollmentId] = useState("");
  const [enrollmentTerm, setEnrollmentTerm] = useState("");
  const [enrollmentForm, setEnrollmentForm] = useState(blankEnrollment());
  const [viewingEnrollment, setViewingEnrollment] = useState(null);
  const [pendingSetup, setPendingSetup] = useState(null);
  const [promotionYear, setPromotionYear] = useState(String(Number(CURRENT_YEAR) + 1));
  const [promotionTab, setPromotionTab] = useState("targets");
  const [promotionTargetCategory, setPromotionTargetCategory] = useState("regular");
  const [schoolChangeDrafts, setSchoolChangeDrafts] = useState({});
  const [graduationChangeDrafts, setGraduationChangeDrafts] = useState({});
  const [editingSchoolChangeId, setEditingSchoolChangeId] = useState("");
  const [editingGraduationChangeId, setEditingGraduationChangeId] = useState("");
  const [scoreChart, setScoreChart] = useState(null);
  const [scoreChartLoading, setScoreChartLoading] = useState(false);
  const [scoreChartError, setScoreChartError] = useState("");
  const [studentPayment, setStudentPayment] = useState(null);
  const [studentPaymentError, setStudentPaymentError] = useState("");

  useEffect(() => {
    setPageTitleOverride?.(pageMode === "detail" ? "학생 상세 정보" : "");
    return () => setPageTitleOverride?.("");
  }, [pageMode, setPageTitleOverride]);

  const selectedStudent = state.students.find((student) => student.id === selectedStudentId) || null;
  const teachers = (state.teachers || []).filter((teacher) => teacher.active !== false);
  const subjects = state.subjects || [];
  const courses = state.courseGroups || [];
  const enrollments = state.studentEnrollments || [];
  const originalEnrollmentCourseId = editingEnrollmentId ? enrollments.find(item => item.id === editingEnrollmentId)?.courseGroupId || enrollments.find(item => item.id === editingEnrollmentId)?.courseId : "";
  const enrollmentTerms = enrollmentCourseTerms(courses, state.courseTerms || [], originalEnrollmentCourseId);
  const enrollmentCourses = courses.filter(course => (course.active !== false || course.id === originalEnrollmentCourseId) && enrollmentTermKey(course) === enrollmentTerm);
  const studentSchoolOptions = useMemo(
    () => [...new Map([
      ...getStudentSchoolOptions(state.academyInfo),
      ...state.students.filter((student) => student.school).map((student) => ({ value: student.school, label: studentSchoolName(student.school, state.academyInfo) })),
    ].map((option) => [option.value, option])).values()],
    [state.academyInfo, state.students]
  );
  const studentGradeOptions = useMemo(() => [...new Set(state.students.map((student) => getStudentGradeLabel(student)).filter((grade) => grade && grade !== "-"))]
    .sort((firstGrade, secondGrade) => firstGrade.localeCompare(secondGrade, "ko"))
    .map((grade) => ({ value: grade, label: grade })), [state.students]);
  const students = useMemo(() => {
    const currentCourseTypesByStudent = new Map();
    enrollments.forEach((enrollment) => {
      if (getEnrollmentStatus(enrollment) !== "수강 중") return;
      const course = courses.find((item) => item.id === (enrollment.courseGroupId || enrollment.courseId));
      if (!course) return;
      const courseTypes = currentCourseTypesByStudent.get(enrollment.studentId) || new Set();
      courseTypes.add(course.courseType === "special" ? "special" : "regular");
      currentCourseTypesByStudent.set(enrollment.studentId, courseTypes);
    });

    function matchesCourseType(studentId) {
      const courseTypes = currentCourseTypesByStudent.get(studentId) || new Set();
      const hasRegular = courseTypes.has("regular");
      const hasSpecial = courseTypes.has("special");
      if (studentFilter.courseType === "special_only") return hasSpecial && !hasRegular;
      if (studentFilter.courseType === "regular_only") return hasRegular && !hasSpecial;
      if (studentFilter.courseType === "regular_and_special") return hasRegular && hasSpecial;
      return true;
    }

    return state.students
      .filter((student) => !studentFilter.status || student.status === studentFilter.status)
      .filter((student) => !studentFilter.name || student.name.includes(studentFilter.name))
      .filter((student) => !studentFilter.school || student.school === studentFilter.school)
      .filter((student) => !studentFilter.grade || getStudentGradeLabel(student) === studentFilter.grade)
      .filter((student) => matchesCourseType(student.id))
      .sort(compareStudentListOrder);
  }, [courses, enrollments, state.students, studentFilter]);
  const studentPagination = useListPagination("list:students:page", students.length, studentFilter, store.loading || !!store.error);

  const scheduledPromotion = useMemo(
    () => (state.scheduledAcademicYearChanges || []).find((schedule) => schedule.academicYear === promotionYear),
    [state.scheduledAcademicYearChanges, promotionYear]
  );
  const scheduledStudentIds = useMemo(
    () => new Set((scheduledPromotion?.changes || []).map((change) => change.studentId)),
    [scheduledPromotion]
  );
  const promotionPreview = useMemo(() => state.students
    .filter((student) => student.status === "재원" && student.lastAcademicYearUpdate !== promotionYear && !scheduledStudentIds.has(student.id))
    .map((student) => ({ student, promotion: getStudentPromotion(student) }))
    .filter(({ promotion }) => Boolean(promotion)), [state.students, promotionYear, scheduledStudentIds]);
  const promotionSummary = useMemo(() => ({
    regular: promotionPreview.filter((item) => item.promotion.kind === "promotion" && !item.promotion.schoolConfirmationRequired),
    schoolConfirmation: promotionPreview.filter((item) => item.promotion.schoolConfirmationRequired),
    graduation: promotionPreview.filter((item) => item.promotion.kind === "graduation")
  }), [promotionPreview]);
  const pendingSchoolConfirmations = promotionSummary.schoolConfirmation;
  const pendingGraduationConfirmations = promotionSummary.graduation;
  const scheduledRegularPromotions = useMemo(() => (scheduledPromotion?.changes || [])
    .filter((change) => getScheduledChangeSource(change) === "regular")
    .map((change) => {
      const student = state.students.find((item) => item.id === change.studentId);
      return student ? { student, promotion: { schoolLevel: change.schoolLevel, grade: change.grade }, reserved: true } : null;
    })
    .filter(Boolean), [scheduledPromotion, state.students]);
  const regularPromotionRows = [...promotionSummary.regular, ...scheduledRegularPromotions];
  const scheduledReservationRows = useMemo(() => (scheduledPromotion?.changes || [])
    .map((change) => {
      const student = state.students.find((item) => item.id === change.studentId);
      if (!student) return null;
      const source = getScheduledChangeSource(change);
      const sourceLabel = source === "regular" ? "일반 학년 변경" : source === "schoolConfirmation" ? "진학 학교 확인" : "졸업·재원 확인";
      const result = source === "graduation"
        ? change.result === "repeat" ? "재원(재수)" : "졸업"
        : `${change.schoolLevel} ${change.grade}학년${source === "schoolConfirmation" ? ` · ${studentSchoolName(change.school, state.academyInfo)}` : ""}`;
      return { key: change.studentId, student, change, source, sourceLabel, result, createdAt: scheduledPromotion.createdAt };
    })
    .filter(Boolean)
    .sort((first, second) => first.student.name.localeCompare(second.student.name, "ko")), [scheduledPromotion, state.students]);
  const promotionYearOptions = useMemo(() => [1, 2, 3].map((offset) => {
    const year = String(Number(CURRENT_YEAR) + offset);
    return { value: year, label: `${year}학년도` };
  }), []);

  const scores = state.scores.filter((score) => score.studentId === selectedStudentId).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const scoreVersion = scores.map((score) => `${score.id}:${score.date}:${score.score}`).join("|");
  const reloadScoreChart = useCallback(async () => {
    if (!selectedStudentId) {
      setScoreChart(null);
      return;
    }
    setScoreChartLoading(true);
    setScoreChartError("");
    try {
      setScoreChart(await rpc("get_student_score_chart", {
        target_student_id: selectedStudentId,
        filter_subject_id: null,
        from_date: null,
        to_date: null,
      }));
    } catch (failure) {
      setScoreChart(null);
      setScoreChartError(failure.message);
    } finally {
      setScoreChartLoading(false);
    }
  }, [selectedStudentId]);
  useEffect(() => {
    reloadScoreChart();
  }, [reloadScoreChart, scoreVersion]);
  useEffect(() => {
    let active = true;
    if (!manager || !selectedStudentId) {
      setStudentPayment(null);
      setStudentPaymentError("");
      return () => { active = false; };
    }
    setStudentPaymentError("");
    paymentDetail(getCurrentMonthKey(), selectedStudentId)
      .then((detail) => {
        if (active) setStudentPayment(detail);
      })
      .catch((failure) => {
        if (active) {
          setStudentPayment(null);
          setStudentPaymentError(failure.message);
        }
      });
    return () => { active = false; };
  }, [manager, selectedStudentId]);
  const consultations = state.consultations.filter((item) => item.studentId === selectedStudentId).sort((a, b) => b.date.localeCompare(a.date));
  const currentMonth = getCurrentMonthKey();
  const selectedEnrollmentHistories = useMemo(() => [...enrollments, ...(state.pendingStudentEnrollments || [])]
    .filter((item) => item.studentId === selectedStudentId)
    .map((enrollment) => ({
      ...enrollment,
      course: courses.find((course) => course.id === (enrollment.courseGroupId || enrollment.courseId)) || null,
      status: enrollment.pending ? "입력 대기" : getEnrollmentStatus(enrollment)
    })), [courses, enrollments, state.pendingStudentEnrollments, selectedStudentId]);
  const enrollmentHistoryRows = [...selectedEnrollmentHistories].sort((first, second) => {
    const statusOrder = { "수강 중": 0, 예정: 1, "입력 대기": 2, 종료: 3 };
    const statusDifference = statusOrder[first.status] - statusOrder[second.status];
    if (statusDifference) return statusDifference;
    if (first.status === "예정") return (first.startDate || "").localeCompare(second.startDate || "");
    if (first.status === "종료") return (second.endDate || "").localeCompare(first.endDate || "");
    return (second.startDate || "").localeCompare(first.startDate || "");
  });
  const currentMonthTuitionEnrollments = selectedEnrollmentHistories.filter((enrollment) => (
    enrollment.startDate?.slice(0, 7) <= currentMonth && (!enrollment.endDate || enrollment.endDate.slice(0, 7) >= currentMonth)
  ));
  const monthlyTuitionTotal = currentMonthTuitionEnrollments.reduce((sum, enrollment) => {
    const isFirstMonth = enrollment.startDate?.slice(0, 7) === currentMonth;
    return sum + Number(isFirstMonth && enrollment.firstMonthAdjustment !== null ? enrollment.firstMonthAdjustment : enrollment.appliedTuition || 0);
  }, 0);
  const hasFirstMonthAdjustment = currentMonthTuitionEnrollments.some((enrollment) => enrollment.startDate?.slice(0, 7) === currentMonth && enrollment.firstMonthAdjustment !== null);
  const hasEndDateThisMonth = currentMonthTuitionEnrollments.some((enrollment) => enrollment.endDate?.slice(0, 7) === currentMonth);
  const routePanel = routeStudentId ? navigation.route.panel : "";
  const routeEntry = routeStudentId ? navigation.route.entry : "";
  const panelItem = routePanel === "score" ? scores.find(item => item.id === routeEntry)
    : routePanel === "enrollment" ? selectedEnrollmentHistories.find(item => item.id === routeEntry) : null;
  const restoredPanel = useRef(false);
  useEffect(() => {
    if (!routePanel || !panelItem || store.loading || selectedStudentId !== routeStudentId || restoredPanel.current) return;
    restoredPanel.current = true;
    if (routePanel === "score") setSelectedScore(panelItem);
    if (routePanel === "enrollment") setViewingEnrollment(panelItem);
  }, [routePanel, panelItem, store.loading, selectedStudentId, routeStudentId]);
  function closeRecordPanel(kind) {
    if (routePanel === kind) { navigation.close(); return; }
    if (kind === "score") setSelectedScore(null);
    else setViewingEnrollment(null);
  }

  function updateStudentFilter(patch) {
    const nextFilter = { ...studentFilterInput, ...patch };
    setStudentFilterInput(nextFilter);
    setStudentFilter({ ...nextFilter, name: nextFilter.name.trim() });
  }

  function resetStudentFilters() {
    setStudentFilterInput(defaultStudentFilter);
    setStudentFilter(defaultStudentFilter);
  }

  function goList() {
    if (routeStudentId) { navigation.close(); return; }
    setPageMode("list");
    setEditingId("");
    setForm(blankStudent);
    setSelectedScore(null);
    setScoreModalOpen(false);
    setConsultationEditForm(null);
  }

  function openCreate() {
    registrationId.current = createId("stu");
    setEditingId("");
    setForm(blankStudent);
    setPageMode("create");
  }

  function openDetail(id) {
    if (navigation) { navigation.open("student", id); return; }
    setSelectedStudentId(id);
    setActiveTab("chart");
    setPageMode("detail");
  }

  function openEdit(student) {
    setEditingId(student.id);
    setForm({ ...blankStudent, ...student });
    setPageMode("edit");
  }

  function openEnrollmentCreate() {
    setEnrollmentSaveError(""); setEnrollmentSavedWarning("");
    setEditingEnrollmentId("");
    setEnrollmentForm(blankEnrollment());
    setEnrollmentTerm(enrollmentCourseTerms(courses, state.courseTerms || [])[0]?.value || "");
    setEnrollmentModalOpen(true);
  }

  function openEnrollmentEdit(enrollment) {
    setEnrollmentSaveError(""); setEnrollmentSavedWarning("");
    if (enrollment.pending) { setPendingSetup(enrollment); return; }
    setEditingEnrollmentId(enrollment.id);
    const originalCourse = courses.find(course => course.id === (enrollment.courseGroupId || enrollment.courseId));
    setEnrollmentTerm(originalCourse ? enrollmentTermKey(originalCourse) : "");
    setEnrollmentForm({
      courseGroupId: enrollment.courseGroupId || enrollment.courseId,
      startDate: enrollment.startDate,
      endDate: enrollment.endDate || "",
      endReason: enrollment.endReason || "",
      endReasonNote: enrollment.endReasonNote || "",
      appliedTuition: enrollment.appliedTuition ?? "",
      firstMonthAdjustment: enrollment.firstMonthAdjustment ?? "",
      memo: enrollment.memo || enrollment.discountMemo || ""
    });
    setEnrollmentModalOpen(true);
  }

  function updateEnrollmentCourse(courseGroupId) {
    const course = courses.find((item) => item.id === courseGroupId);
    setEnrollmentForm((current) => ({ ...current, courseGroupId, appliedTuition: Number(course?.tuition || 0) }));
  }

  async function saveEnrollment() {
    if (store.saving || enrollmentSavedWarning) return;
    setEnrollmentSaveError("");
    if (!selectedStudent || !enrollmentForm.courseGroupId || !enrollmentForm.startDate || manager && enrollmentForm.appliedTuition === "") {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "수업명, 실제 수강 시작일, 월 정상 수강료를 입력하세요", centered: true });
      return;
    }
    if (enrollmentForm.endDate && enrollmentForm.endDate < enrollmentForm.startDate) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "실제 수강 종료일은 시작일보다 빠를 수 없습니다", centered: true });
      return;
    }
    if (enrollmentForm.endDate && !enrollmentForm.endReason) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "수강 종료 사유를 선택하세요", centered: true });
      return;
    }
    if (enrollmentForm.endReason === "기타" && !enrollmentForm.endReasonNote.trim()) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "기타 수강 종료 사유를 입력하세요", centered: true });
      return;
    }
    let reloadWarning = false;
    if (!await update((draft) => {
      const payload = {
        courseGroupId: enrollmentForm.courseGroupId,
        courseId: enrollmentForm.courseGroupId,
        startDate: enrollmentForm.startDate,
        endDate: enrollmentForm.endDate || "",
        endReason: enrollmentForm.endDate ? enrollmentForm.endReason : "",
        endReasonNote: enrollmentForm.endDate && enrollmentForm.endReason === "기타" ? enrollmentForm.endReasonNote.trim() : "",
        startMonth: enrollmentForm.startDate.slice(0, 7),
        endMonth: enrollmentForm.endDate ? enrollmentForm.endDate.slice(0, 7) : "",
        appliedTuition: Number(enrollmentForm.appliedTuition || 0),
        firstMonthAdjustment: enrollmentForm.firstMonthAdjustment === "" ? null : Number(enrollmentForm.firstMonthAdjustment),
        memo: enrollmentForm.memo || "",
        discountMemo: enrollmentForm.memo || "",
        updatedAt: todayKey(),
        active: !enrollmentForm.endDate || enrollmentForm.endDate >= todayKey()
      };
      if (editingEnrollmentId) {
        const enrollment = draft.studentEnrollments.find((item) => item.id === editingEnrollmentId);
        if (enrollment) Object.assign(enrollment, payload);
      } else {
        draft.studentEnrollments.push({ id: createId("enroll"), studentId: selectedStudent.id, createdAt: todayKey(), ...payload });
      }
    }, { onError: setEnrollmentSaveError, onSavedWarning: warning => { reloadWarning = true; setEnrollmentSavedWarning(warning); } })) return;
    if (reloadWarning) return;
    setEnrollmentModalOpen(false);
    if (manager && editingEnrollmentId) {
      try {
        const result = await rpc("get_first_month_charge_refresh_status", { target_enrollment_id: editingEnrollmentId });
        if (result && !result.applied && result.reason !== "not_generated") {
          const reason = {
            active_payment: "납부 내역이 남아 있어",
            active_or_unconfirmed_invoice: "진행 중이거나 종료가 확인되지 않은 청구가 있어",
            processing_or_unknown: "청구·취소 처리가 끝났는지 확인이 필요해",
            incomplete_cancellation: "결제 취소·환불이 완전히 끝나지 않아",
            legacy_history: "기존 청구 기록의 확인이 필요해",
            snapshot_not_eligible: "기존 금액 또는 수강 기간의 확인이 필요해",
          }[result.reason] || "기존 청구·납부 기록의 확인이 필요해";
          message.warning(`수강 정보는 저장했습니다. ${reason} 납부 금액은 유지했습니다.`, 6);
        }
      } catch {
        message.info("수강 정보는 저장했습니다. 납부 금액 반영 여부는 납부 관리에서 새로고침 후 확인해 주세요.", 6);
      }
    }
  }

  function showEnrollmentDetail(enrollment) {
    if (routeStudentId) { navigation.openPanel("enrollment", enrollment.id); return; }
    setViewingEnrollment(enrollment);
  }

  function endEnrollment(enrollment) {
    if (!enrollment) return;
    setViewingEnrollment(null);
    openEnrollmentEdit(enrollment);
    setEnrollmentForm((current) => ({
      ...current,
      endDate: current.endDate || todayKey(),
      endReason: current.endReason || "수강 종료",
    }));
  }

  function renderEnrollmentHistoryTable(rows) {
    return (
      <Table
        rowKey="id"
        size="small"
        dataSource={rows}
        pagination={false}
        scroll={{ x: 780 }}
        onRow={(enrollment) => ({ onClick: () => showEnrollmentDetail(enrollment), style: { cursor: "pointer" } })}
        locale={{ emptyText: <AppEmpty description="등록된 수강 이력이 없습니다." compact /> }}
        columns={[
          { title: "유형", width: 72, render: (_, enrollment) => enrollment.course?.courseType === "special" ? "특강" : "정규" },
          { title: "수업명", render: (_, enrollment) => enrollment.course?.className || "수업 미지정" },
          { title: "수강 기간", render: (_, enrollment) => enrollment.pending ? "-" : `${dayjs(enrollment.startDate).format("YYYY.MM.DD")} ~ ${enrollment.endDate ? dayjs(enrollment.endDate).format("YYYY.MM.DD") : "수강 중"}` },
          { title: "월 정상 수강료", render: (_, enrollment) => manager ? formatWon(enrollment.appliedTuition) : "-" },
          { title: "첫 달 조정", render: (_, enrollment) => enrollment.pending || !manager || enrollment.firstMonthAdjustment === null ? "-" : formatWon(enrollment.firstMonthAdjustment) },
          { title: "상태", render: (_, enrollment) => renderEnrollmentStatusTag(enrollment.status) }
        ]}
      />
    );
  }

  function openConsultationCreate() {
    if (!selectedStudent) return;
    setConsultationForm(newConsultationForm(selectedStudent.id));
    setConsultationFileList([]);
    setPageMode("consultationCreate");
  }

  function openConsultationDetail(item) {
    setConsultationEditForm({ ...newConsultationForm(selectedStudentId), ...item, files: item.files || item.photos || [] });
    setConsultationEditFileList([]);
  }

  async function commitStudent(studentForm) {
    let nextId = editingId;
    if (!editingId) {
      registrationId.current ||= createId("stu");
      nextId = await store.registerStudent(registrationId.current, studentForm);
      if (!nextId) return;
      registrationId.current = null;
      if (navigation) { navigation.open("student", nextId); return; }
      setSelectedStudentId(nextId);
      setForm(blankStudent);
      setPageMode("detail");
      return;
    }
    if (!await update((draft) => {
      if (editingId) {
        const student = draft.students.find((item) => item.id === editingId);
        Object.assign(student, studentForm);
      } else {
        nextId = createId("stu");
        draft.students.push({ id: nextId, ...studentForm });
      }
    })) return;
    setSelectedStudentId(nextId);
    setForm(blankStudent);
    setEditingId("");
    setPageMode("detail");
  }

  function saveStudent() {
    const studentForm = { ...form };

    if (!studentForm.name.trim() || !studentForm.schoolLevel || (studentForm.schoolLevel !== "재수" && !studentForm.schoolConfirmationRequired && !studentForm.school.trim()) || (studentForm.schoolLevel !== "재수" && !studentForm.grade)) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "필수값을 입력하세요", content: studentForm.schoolLevel === "재수" || studentForm.schoolConfirmationRequired ? "이름, 과정은 필수입니다." : "이름, 과정, 학교, 학년은 필수입니다.", centered: true });
      return;
    }
    if (studentForm.parentPhone && !["부", "모"].includes(studentForm.guardianRelationship)) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "보호자 관계를 선택하세요", content: "보호자 연락처를 입력했다면 부 또는 모를 선택해 주세요.", centered: true });
      return;
    }
    const duplicate = state.students.find((student) => student.id !== editingId && student.name === studentForm.name && student.school === studentForm.school && student.schoolLevel === studentForm.schoolLevel && student.grade === studentForm.grade);
    if (duplicate) {
      modal.confirm({
      className: "student-management-modal",
        title: "동일한 학생 정보가 있습니다",
        content: "이름, 학교, 학년이 같은 학생이 이미 등록되어 있습니다. 그래도 저장할까요?",
        okText: "계속 저장",
        cancelText: "취소",
        centered: true,
        onOk: () => commitStudent(studentForm)
      });
      return;
    }
    commitStudent(studentForm);
  }

  async function saveScore() {
    if (!selectedStudentId) return message.warning("학생을 먼저 선택하세요.");
    const score = Number(scoreForm.score);
    const maxScore = Number(scoreForm.maxScore || 100);
    if (Number.isNaN(score) || score < 0 || score > maxScore) return message.warning("점수는 0 이상, 만점 이하로 입력해야 합니다.");
    try {
      const files = await readLimitedFiles(scoreFileList.map((file) => file.originFileObj).filter(Boolean), { maxCount: 5, types: ["image/jpeg", "image/png", "application/pdf"] });
      if (!await update((draft) => {
        draft.scores.push({ id: createId("score"), studentId: selectedStudentId, ...scoreForm, score, maxScore, files });
      })) return;
      setScoreForm(blankScore);
      setScoreFileList([]);
      setScoreModalOpen(false);
    } catch (error) {
      message.error(error.message || "파일을 처리하지 못했습니다.");
    }
  }

  async function saveConsultation() {
    if (!selectedStudent) return message.warning("학생을 먼저 선택하세요.");
    if (!consultationForm.content.trim()) return message.warning("상담 내용을 입력하세요.");
    try {
      const files = await readLimitedFiles(consultationFileList.map((file) => file.originFileObj).filter(Boolean), { maxCount: 5, types: ["image/jpeg", "image/png", "application/pdf"] });
      if (!await update((draft) => {
        draft.consultations.push({ id: createId("con"), ...consultationForm, studentId: selectedStudent.id, files });
      })) return;
      setConsultationForm(newConsultationForm(selectedStudent.id));
      setConsultationFileList([]);
      setActiveTab("consults");
      setPageMode("detail");
    } catch (error) {
      message.error(error.message || "파일을 처리하지 못했습니다.");
    }
  }

  async function saveConsultationEdit() {
    if (!consultationEditForm) return;
    if (!consultationEditForm.content.trim()) return message.warning("상담 내용을 입력하세요.");
    try {
      const nextFiles = await readLimitedFiles(consultationEditFileList.map((file) => file.originFileObj).filter(Boolean), { maxCount: 5, types: ["image/jpeg", "image/png", "application/pdf"] });
      if (!await update((draft) => {
        const item = draft.consultations.find((target) => target.id === consultationEditForm.id);
        item.date = consultationEditForm.date;
        item.type = consultationEditForm.type;
        item.content = consultationEditForm.content;
        item.files = nextFiles.length ? nextFiles : consultationEditForm.files || [];
        delete item.photos;
      })) return;
      setConsultationEditForm(null);
      setConsultationEditFileList([]);
    } catch (error) {
      message.error(error.message || "파일을 처리하지 못했습니다.");
    }
  }

  function removeConsultationAttachment(fileId) {
    setConsultationEditForm((current) => {
      if (!current) return current;
      return {
        ...current,
        files: (current.files || []).filter((file) => file.id !== fileId),
      };
    });
  }

  function openScoreModal() {
    setScoreForm(blankScore);
    setScoreFileList([]);
    setScoreModalOpen(true);
  }

  function removeScore(id) {
    modal.confirm({
      className: "student-management-modal",
      title: "시험 기록을 삭제할까요?",
      content: "삭제한 시험 기록은 복구할 수 없습니다.",
      okText: "삭제",
      cancelText: "취소",
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        if (!await update((draft) => {
          draft.scores = draft.scores.filter((score) => score.id !== id);
        })) return;
        if (selectedScore?.id === id) setSelectedScore(null);
      }
    });
  }

  function removeConsultation(id) {
    modal.confirm({
      className: "student-management-modal",
      title: "상담 기록을 삭제할까요?",
      content: "삭제한 상담 기록은 복구할 수 없습니다.",
      okText: "삭제",
      cancelText: "취소",
      okButtonProps: { danger: true },
      centered: true,
      onOk: async () => {
        if (!await update((draft) => {
          draft.consultations = draft.consultations.filter((item) => item.id !== id);
        })) return;
        if (consultationEditForm?.id === id) setConsultationEditForm(null);
      }
    });
  }

  function openPromotionPage() {
    setPromotionYear(String(Number(CURRENT_YEAR) + 1));
    setSchoolChangeDrafts({});
    setGraduationChangeDrafts({});
    setEditingSchoolChangeId("");
    setEditingGraduationChangeId("");
    setPromotionTab("targets");
    setPromotionTargetCategory("regular");
    setPageMode("promotion");
  }

  function changePromotionYear(year) {
    setPromotionYear(year);
    setSchoolChangeDrafts({});
    setGraduationChangeDrafts({});
    setEditingSchoolChangeId("");
    setEditingGraduationChangeId("");
    setPromotionTab("targets");
    setPromotionTargetCategory("regular");
  }

  async function reservePromotionChanges(changes) {
    if (!await update((draft) => {
      const existingSchedule = (draft.scheduledAcademicYearChanges || []).find((schedule) => schedule.academicYear === promotionYear);
      const nextChanges = [
        ...(existingSchedule?.changes || []).filter((scheduledChange) => !changes.some((change) => change.studentId === scheduledChange.studentId)),
        ...changes
      ];
      draft.scheduledAcademicYearChanges = [
        ...(draft.scheduledAcademicYearChanges || []).filter((schedule) => schedule.academicYear !== promotionYear),
        { academicYear: promotionYear, createdAt: existingSchedule?.createdAt || new Date().toISOString(), changes: nextChanges }
      ];
    })) return;
  }

  async function removeReservedPromotionChange(studentId) {
    if (!await update((draft) => {
      const existingSchedule = (draft.scheduledAcademicYearChanges || []).find((schedule) => schedule.academicYear === promotionYear);
      if (!existingSchedule) return;
      const nextChanges = existingSchedule.changes.filter((change) => change.studentId !== studentId);
      draft.scheduledAcademicYearChanges = [
        ...(draft.scheduledAcademicYearChanges || []).filter((schedule) => schedule.academicYear !== promotionYear),
        ...(nextChanges.length ? [{ ...existingSchedule, changes: nextChanges }] : [])
      ];
    })) return;
  }

  function cancelReservedPromotionChange(reservation) {
    AntModal.confirm({
      className: "student-management-modal",
      title: "예약을 취소할까요?",
      content: `${reservation.student.name} 학생의 예약 변경이 변경 대상 목록으로 돌아갑니다.`,
      centered: true,
      onOk: () => {
        removeReservedPromotionChange(reservation.student.id);
        setPromotionTab("targets");
      }
    });
  }

  function completeSchoolChange(student, promotion) {
    const studentId = student.id;
    const school = schoolChangeDrafts[studentId];
    if (!school) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "진학 학교를 선택하세요", centered: true });
      return;
    }
    reservePromotionChanges([{
      studentId,
      source: "schoolConfirmation",
      result: "promotion",
      schoolLevel: promotion.schoolLevel,
      grade: promotion.grade,
      school
    }]);
    setEditingSchoolChangeId("");
  }

  function completeGraduationChange(student) {
    const studentId = student.id;
    const choice = graduationChangeDrafts[studentId];
    if (!choice) {
      AntModal.warning({ className: "student-management-modal", okText: "확인", title: "변경 결과를 선택하세요", centered: true });
      return;
    }
    reservePromotionChanges([choice === "repeat"
      ? { studentId, source: "graduation", result: choice, schoolLevel: "재수", grade: "", school: "" }
      : { studentId, source: "graduation", result: choice }
    ]);
    setEditingGraduationChangeId("");
  }

  useEffect(() => {
    if (pageMode !== "promotion" || !promotionSummary.regular.length) return;
    reservePromotionChanges(promotionSummary.regular.map(({ student, promotion }) => ({
      studentId: student.id,
      source: "regular",
      result: "promotion",
      schoolLevel: promotion.schoolLevel,
      grade: promotion.grade
    })));
  }, [pageMode, promotionSummary.regular, promotionYear]);

  const studentColumns = [
    { title: "이름", dataIndex: "name", fixed: "left" },
    {
      title: "학교",
      render: (_, student) => <Space size={4} wrap><span>{student.schoolLevel === "재수" ? "-" : studentListSchoolName(student.school, state.academyInfo) || "미확정"}</span>{student.schoolConfirmationRequired ? renderStudentReviewTag(student) : null}</Space>
    },
    { title: "학년", render: (_, student) => studentListGrade(student) },
    { title: "상태", render: (_, student) => <Space size={4} wrap>{renderStudentStatusTag(student.status)}{student.graduationReviewRequired ? renderStudentReviewTag(student) : null}</Space> }
  ];

  if (pageMode === "promotion") {
    const promotionTableProps = {
      rowKey: ({ student }) => student.id,
      pagination: { pageSize: 20, showSizeChanger: false },
      size: "middle",
      scroll: { x: 760 }
    };
    const pendingReviewCount = pendingSchoolConfirmations.length + pendingGraduationConfirmations.length;
    const totalPromotionTargets = promotionPreview.length + scheduledReservationRows.length;
    const promotionTargetOptions = [
      { value: "regular", label: <span>일반 학년 변경 <strong>{regularPromotionRows.length}</strong></span> },
      { value: "school", label: <span>진학 학교 확인 <strong>{pendingSchoolConfirmations.length}</strong></span> },
      { value: "graduation", label: <span>졸업·재원 확인 <strong>{pendingGraduationConfirmations.length}</strong></span> }
    ];

    let activeTargetTitle = "일반 학년 변경";
    let activeTargetDescription = "현재 학년을 기준으로 다음 학년도 변경이 자동 예약됩니다.";
    let activeTargetRows = regularPromotionRows;
    let activeTargetColumns = [
      { title: "이름", render: (_, { student }) => student.name, fixed: "left" },
      { title: "학교", render: (_, { student }) => studentSchoolName(student.school, state.academyInfo) || "-" },
      { title: "현재 학년", render: (_, { student }) => getStudentGradeLabel(student) },
      { title: "변경 후 과정", render: (_, { promotion }) => promotion.schoolLevel },
      { title: "변경 후 학년", render: (_, { promotion }) => `${promotion.grade}학년` },
      { title: "예약 상태", width: 100, render: (_, item) => item.reserved ? <StatusTag tone="positive">예약 완료</StatusTag> : <StatusTag tone="neutral">예약 중</StatusTag> }
    ];

    if (promotionTargetCategory === "school") {
      activeTargetTitle = "진학 학교 확인";
      activeTargetDescription = "상급 과정으로 진학하는 학생의 새 학교를 확인해 예약합니다.";
      activeTargetRows = pendingSchoolConfirmations;
      activeTargetColumns = [
        { title: "이름", render: (_, { student }) => student.name, fixed: "left" },
        { title: "현재 학교", render: (_, { student }) => studentSchoolName(student.school, state.academyInfo) || "-" },
        { title: "현재 학년", render: (_, { student }) => getStudentGradeLabel(student) },
        { title: "변경 후", render: (_, { promotion }) => `${promotion.schoolLevel} ${promotion.grade}학년` },
        { title: "진학 학교", width: 220, render: (_, { student, promotion }) => editingSchoolChangeId === student.id ? (
          <Select value={schoolChangeDrafts[student.id] || undefined} onChange={(school) => setSchoolChangeDrafts((current) => ({ ...current, [student.id]: school || "" }))} options={getStudentSchoolOptions(state.academyInfo, promotion.schoolLevel)} placeholder="학교 검색" showSearch filterOption={matchesSchoolSearch} />
        ) : <StatusTag tone="warning">학교 미확정</StatusTag> },
        { title: "관리", width: 84, render: (_, { student, promotion }) => editingSchoolChangeId === student.id ? (
          <Button type="primary" size="small" onClick={() => completeSchoolChange(student, promotion)}>완료</Button>
        ) : (
          <Button size="small" onClick={() => { setEditingSchoolChangeId(student.id); setEditingGraduationChangeId(""); }}>확인</Button>
        ) }
      ];
    }

    if (promotionTargetCategory === "graduation") {
      activeTargetTitle = "졸업·재원 확인";
      activeTargetDescription = "졸업 예정 학생의 졸업 또는 재수 재원 여부를 확인합니다.";
      activeTargetRows = pendingGraduationConfirmations;
      activeTargetColumns = [
        { title: "이름", render: (_, { student }) => student.name, fixed: "left" },
        { title: "학교", render: (_, { student }) => studentSchoolName(student.school, state.academyInfo) || "-" },
        { title: "현재 학년", render: (_, { student }) => getStudentGradeLabel(student) },
        { title: "변경 결과", width: 240, render: (_, { student }) => editingGraduationChangeId === student.id ? (
          <Radio.Group value={graduationChangeDrafts[student.id]} onChange={(event) => setGraduationChangeDrafts((current) => ({ ...current, [student.id]: event.target.value }))} options={[{ value: "repeat", label: "재원(재수)" }, { value: "graduate", label: "졸업" }]} optionType="button" buttonStyle="solid" />
        ) : <StatusTag tone="warning">확인 필요</StatusTag> },
        { title: "관리", width: 84, render: (_, { student }) => editingGraduationChangeId === student.id ? (
          <Button type="primary" size="small" onClick={() => completeGraduationChange(student)}>완료</Button>
        ) : (
          <Button size="small" onClick={() => { setEditingGraduationChangeId(student.id); setEditingSchoolChangeId(""); }}>확인</Button>
        ) }
      ];
    }

    return (
      <div className="page-stack promotion-page student-management-page">
        <div className="promotion-back-row">
          <Button icon={<ArrowLeft size={16} />} onClick={goList}>학생 목록</Button>
        </div>

        <section className="promotion-period-context">
          <div className="promotion-context-identity">
            <strong>학년 변경 예약</strong>
            <span>학년·진학 학교·졸업 여부를 확인하고 다음 학년도 변경을 준비합니다.</span>
          </div>
          <div className="promotion-year-context">
            <Select className="promotion-year-select" size="large" value={promotionYear} onChange={changePromotionYear} options={promotionYearOptions} aria-label="변경 대상 학년도" />
          </div>
        </section>

        <Card className="academy-management-card promotion-overview-card">
          <div className="promotion-overview-header">
            <div><h3>변경 현황 요약</h3><span>{promotionYear}학년도 기준</span></div>
          </div>
          <div className="promotion-overview-metrics" aria-label="학년 변경 현황 요약">
            <div className="promotion-overview-metric"><span>전체 대상</span><strong>{totalPromotionTargets}명</strong></div>
            <div className="promotion-overview-metric is-auto"><span>자동 학년 변경</span><strong>{regularPromotionRows.length}명</strong></div>
            <div className="promotion-overview-metric is-pending"><span>관리자 확인 필요</span><strong>{pendingReviewCount}명</strong></div>
            <div className="promotion-overview-metric is-reserved"><span>예약 완료</span><strong>{scheduledReservationRows.length}명</strong></div>
          </div>
        </Card>

        <Card className="academy-management-card promotion-workspace-card">
          <div className="academy-tab-panel promotion-list-panel">
            <Tabs
              className="promotion-tabs"
              type="line"
              activeKey={promotionTab}
              onChange={setPromotionTab}
              items={[
                {
                  key: "targets",
                  label: "변경 대상",
                  children: (
                    <div className="promotion-target-workspace">
                      <Segmented
                        className="promotion-target-segmented"
                        value={promotionTargetCategory}
                        onChange={(value) => {
                          setPromotionTargetCategory(value);
                          setEditingSchoolChangeId("");
                          setEditingGraduationChangeId("");
                        }}
                        options={promotionTargetOptions}
                        aria-label="학년 변경 대상 유형"
                      />
                      <div className="promotion-target-heading">
                        <div><h3>{activeTargetTitle}</h3><span>{activeTargetDescription}</span></div>
                        <strong>{activeTargetRows.length}명</strong>
                      </div>
                      <Table {...promotionTableProps} dataSource={activeTargetRows} locale={{ emptyText: <AppEmpty description="해당 유형의 변경 대상이 없습니다." /> }} columns={activeTargetColumns} />
                    </div>
                  )
                },
                {
                  key: "reserved",
                  label: `예약된 변경 (${scheduledReservationRows.length})`,
                  children: (
                    <div className="promotion-target-workspace">
                      <div className="promotion-target-heading">
                        <div><h3>예약된 변경</h3><span>{promotionYear}학년도에 적용될 학생별 변경 내역입니다.</span></div>
                        <strong>{scheduledReservationRows.length}명</strong>
                      </div>
                      <Table rowKey="key" size="middle" dataSource={scheduledReservationRows} pagination={{ pageSize: 20, showSizeChanger: false }} scroll={{ x: 760 }} locale={{ emptyText: <AppEmpty description="예약된 변경이 없습니다." /> }} columns={[
                        { title: "구분", dataIndex: "sourceLabel" },
                        { title: "이름", render: (_, reservation) => reservation.student.name, fixed: "left" },
                        { title: "현재 정보", render: (_, reservation) => `${studentSchoolName(reservation.student.school, state.academyInfo) || "-"} · ${getStudentGradeLabel(reservation.student)}` },
                        { title: "예약 변경", dataIndex: "result" },
                        { title: "예약일", render: (_, reservation) => formatDate(reservation.createdAt) },
                        { title: "관리", width: 72, render: (_, reservation) => reservation.source === "regular" ? "-" : <Button type="link" danger size="small" onClick={() => cancelReservedPromotionChange(reservation)}>취소</Button> }
                      ]} />
                    </div>
                  )
                }
              ]}
            />
          </div>
        </Card>
      </div>
    );
  }
  if (pageMode === "create" || pageMode === "edit") {
    return (
      <div className="page-stack student-editor-page student-management-page">
        <div className="detail-heading">
          <Button icon={<ArrowLeft size={16} />} onClick={pageMode === "edit" && selectedStudent ? () => setPageMode("detail") : goList}>{pageMode === "edit" ? "학생 정보" : "학생 목록"}</Button>
        </div>
        <Card className="academy-management-card student-editor-card" title={<div className="student-editor-card-title"><strong>{editingId ? "학생 정보 수정" : "학생 등록"}</strong><span>{editingId ? "학생의 기본 정보와 연락처를 수정합니다." : "학생 관리에 필요한 기본 정보부터 순서대로 입력합니다."}</span></div>}>
          <div className="academy-tab-panel">
            <StudentEditor
              editingId={editingId}
              form={form}
              setForm={setForm}
              onSubmit={saveStudent}
              onCancel={pageMode === "edit" && selectedStudent ? () => setPageMode("detail") : goList}
              academyInfo={state.academyInfo} billingVisible={manager}
              canManageSchools={manager} refreshSchools={store.refreshSchools}
              courses={courses} subjects={subjects} teachers={teachers} saving={store.saving}
            />
          </div>
        </Card>
      </div>
    );
  }

  if (pageMode === "consultationCreate" && selectedStudent) {
    return (
      <div className="page-stack student-management-page">
        <div className="detail-heading">
          <Button icon={<ArrowLeft size={16} />} onClick={() => { setActiveTab("consults"); setPageMode("detail"); }}>상담 일지</Button>
        </div>
        <Panel title="상담 추가" description="선택한 학생의 상담 내용을 기록합니다.">
          <Form layout="vertical" size="medium" onFinish={saveConsultation}>
            <Row gutter={12}>
              <Col xs={24} md={8}>
                <Form.Item label="학생 이름">
                  <Input value={selectedStudent.name} disabled />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="상담 일시" required>
                  <Input type="datetime-local" value={consultationForm.date} onChange={(event) => setConsultationForm({ ...consultationForm, date: event.target.value })} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="분류">
                  <Select
                    value={consultationForm.type}
                    onChange={(type) => setConsultationForm({ ...consultationForm, type })}
                    options={["학생상담", "학부모상담"].map((value) => ({ value, label: value }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="상담 내용" required>
                  <Input.TextArea value={consultationForm.content} onChange={(event) => setConsultationForm({ ...consultationForm, content: event.target.value })} rows={6} />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item label="첨부파일">
                  <Upload multiple beforeUpload={() => false} fileList={consultationFileList} onChange={({ fileList }) => setConsultationFileList(fileList)} accept=".jpg,.jpeg,.png,.pdf">
                    <Button icon={<FileText size={16} />}>파일 선택</Button>
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            <StudentActions>
              <Button onClick={() => { setActiveTab("consults"); setPageMode("detail"); }}>취소</Button>
              <Button type="primary" htmlType="submit" icon={<FileText size={16} />}>상담 저장</Button>
            </StudentActions>
          </Form>
        </Panel>
      </div>
    );
  }

  if (routeStudentId && (!state.students.some(item => item.id === routeStudentId) || selectedStudentId !== routeStudentId)) {
    return <RouteTargetNotice loading={store.loading || (requestedStudent.current !== routeStudentId && state.students.some(item => item.id === routeStudentId))} onBack={() => navigation.close()} />;
  }
  if (pageMode === "detail" && selectedStudent) {
    if (routePanel && !panelItem) return <RouteTargetNotice loading={store.loading} onBack={() => navigation.close()} />;
    return (
      <div className="page-stack student-detail-page student-management-page">
        <div className="detail-heading">
          <Button icon={<ArrowLeft size={16} />} onClick={goList}>학생 목록</Button>
        </div>

        <Card
          className="academy-management-card student-basic-info-card"
          title={<Space><span>{selectedStudent.name}</span>{renderStudentStatusTag(selectedStudent.status)}</Space>}
          extra={<StudentActions><Button type="primary" icon={<Pencil size={16} />} onClick={() => openEdit(selectedStudent)}>수정</Button></StudentActions>}
        >
          <Descriptions column={{ xs: 1, sm: 2, lg: 3 }} size="small">
            <Descriptions.Item label="학교">{studentSchoolName(selectedStudent.school, state.academyInfo) || "-"}</Descriptions.Item>
            <Descriptions.Item label="학년">{getStudentGradeLabel(selectedStudent)}</Descriptions.Item>
            <Descriptions.Item label="등록일">{formatDate(selectedStudent.joinedAt)}</Descriptions.Item>
            <Descriptions.Item label="학생 연락처">{formatStudentContact(selectedStudent.phone) || "-"}</Descriptions.Item>
            <Descriptions.Item label="보호자 관계">{["부", "모"].includes(selectedStudent.guardianRelationship) ? selectedStudent.guardianRelationship : "-"}</Descriptions.Item>
            <Descriptions.Item label="보호자 연락처">{formatStudentContact(selectedStudent.parentPhone) || "-"}</Descriptions.Item>
            {manager && <Descriptions.Item label="주 납부 방식">{selectedStudent.billingMethod || "매번 선택"}</Descriptions.Item>}
            <Descriptions.Item label="성별">{selectedStudent.gender || "-"}</Descriptions.Item>
            {manager && <Descriptions.Item label="이번 달 납부">{studentPayment ? `${({ untouched: "처리 전", billing: "청구 중", paid: "납부 완료", retry: "재처리 필요" }[studentPayment.status] || "-")} · ${formatWon(studentPayment.currentCharge?.remainingAmount ?? studentPayment.currentCharge?.amount)}` : studentPaymentError ? "불러오지 못했습니다" : "불러오는 중"}</Descriptions.Item>}
            <Descriptions.Item label="메모" span={3}>{selectedStudent.memo || "-"}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card
          className="academy-management-card student-detail-section-card"
          title="수강 이력"
          extra={(
            <Space wrap>
              <Button type="primary" icon={<Plus size={16} />} disabled={!manager} onClick={openEnrollmentCreate}>수업 추가</Button>
            </Space>
          )}
        >
          <Flex vertical gap="large">
            {renderEnrollmentHistoryTable(enrollmentHistoryRows)}

            {manager && <Descriptions className="student-tuition-summary" size="small" column={1} bordered>
              <Descriptions.Item label={`${currentMonth.replace("-", "년 ")}월 예상 수강료`}>{formatWon(monthlyTuitionTotal)}</Descriptions.Item>
              <Descriptions.Item label="안내">
                {hasEndDateThisMonth
                  ? "수업 종료일이 포함되어 있어 납부 관리에서 환불 또는 금액 조정이 필요할 수 있습니다."
                  : hasFirstMonthAdjustment
                    ? "중간 등록으로 첫 달 조정 금액이 적용됩니다."
                    : "정상 월 수강료 기준 / 부분월 조정 없음"}
              </Descriptions.Item>
            </Descriptions>}
          </Flex>
        </Card>

        <AntModal
          title={editingEnrollmentId ? "수강 이력 수정" : "수업 추가"}
          className={editingEnrollmentId ? "student-management-modal student-enrollment-edit-modal" : "student-management-modal"}
          open={enrollmentModalOpen}
          onCancel={() => { if (!store.saving) setEnrollmentModalOpen(false); }}
          onOk={saveEnrollment}
          confirmLoading={store.saving}
          okButtonProps={{ disabled: Boolean(enrollmentSavedWarning) }}
          cancelButtonProps={{ disabled: store.saving }}
          closable={!store.saving}
          maskClosable={!store.saving}
          okText={editingEnrollmentId ? "저장" : "추가"}
          cancelText="취소"
          width={760}
          destroyOnHidden
        >
          <Form layout="vertical" disabled={store.saving || Boolean(enrollmentSavedWarning)}>
            <div className="course-detail-table-wrap">
              {enrollmentSaveError && <div ref={enrollmentFeedbackRef} tabIndex={-1}><Alert style={{ marginBottom: 16 }} type="error" showIcon message={enrollmentSaveError} /></div>}
              {enrollmentSavedWarning && <div ref={enrollmentFeedbackRef} tabIndex={-1}><Alert style={{ marginBottom: 16 }} type="warning" showIcon message={enrollmentSavedWarning} /></div>}
              <table className="course-detail-table course-edit-table">
                <colgroup><col /><col /><col /><col /></colgroup>
                <tbody>
                  <tr><th scope="row">수업 학기</th><td colSpan={3}><Form.Item className="course-table-form-item" required><Select aria-label="수업 학기" value={enrollmentTerm || undefined} options={enrollmentTerms} placeholder="선택 가능한 학기가 없습니다" onChange={(value) => { setEnrollmentTerm(value); setEnrollmentForm(current => ({ ...current, courseGroupId: "", appliedTuition: "" })); }} /></Form.Item></td></tr>
                  <tr><th scope="row">수업명</th><td colSpan={3}><Form.Item className="course-table-form-item" required><Select aria-label="수업 선택" showSearch optionFilterProp="label" value={enrollmentForm.courseGroupId || undefined} onChange={updateEnrollmentCourse} options={enrollmentCourses.map((course) => ({ value: course.id, label: enrollmentCourseLabel(course, subjects, teachers) }))} placeholder="수업명 선택" notFoundContent="선택 가능한 수업이 없습니다" /></Form.Item></td></tr>
                  <tr><th scope="row">수강 시작일</th><td><Form.Item className="course-table-form-item" required><DatePicker locale={koKR} format="YYYY년 M월 D일" value={enrollmentForm.startDate ? dayjs(enrollmentForm.startDate, "YYYY-MM-DD") : null} onChange={(value) => setEnrollmentForm((current) => ({ ...current, startDate: value?.format("YYYY-MM-DD") || "" }))} /></Form.Item></td><th scope="row">수강 종료일</th><td><Form.Item className="course-table-form-item"><DatePicker locale={koKR} format="YYYY년 M월 D일" value={enrollmentForm.endDate ? dayjs(enrollmentForm.endDate, "YYYY-MM-DD") : null} onChange={(value) => setEnrollmentForm((current) => ({ ...current, endDate: value?.format("YYYY-MM-DD") || "" }))} /></Form.Item></td></tr>
                  <tr><th scope="row">월 정상 수강료</th><td><Form.Item className="course-table-form-item" required><InputNumber min={0} disabled={!manager} value={enrollmentForm.appliedTuition === "" ? null : Number(enrollmentForm.appliedTuition)} onChange={(value) => setEnrollmentForm((current) => ({ ...current, appliedTuition: value ?? "" }))} /></Form.Item></td><th scope="row">첫 달 조정 금액</th><td><Form.Item className="course-table-form-item"><InputNumber min={0} disabled={!manager} value={enrollmentForm.firstMonthAdjustment === "" ? null : Number(enrollmentForm.firstMonthAdjustment)} onChange={(value) => setEnrollmentForm((current) => ({ ...current, firstMonthAdjustment: value ?? "" }))} /></Form.Item></td></tr>
                  {enrollmentForm.endDate && <tr><th scope="row">수강 종료 사유</th><td colSpan={3}><Form.Item className="course-table-form-item" required><Radio.Group value={enrollmentForm.endReason} onChange={(event) => setEnrollmentForm((current) => ({ ...current, endReason: event.target.value, endReasonNote: event.target.value === "기타" ? current.endReasonNote : "" }))}><Space direction="vertical"><Radio value="수강 종료">수강 종료</Radio><Radio value="반 이동">반 이동</Radio><Radio value="학년 이동">학년 이동</Radio><Radio value="퇴원">퇴원</Radio><Radio value="수강 완료(특강)">수강 완료(특강)</Radio><div><Radio value="기타">기타</Radio><Input style={{ display: "block", width: 240, marginTop: 4, marginLeft: 24 }} disabled={enrollmentForm.endReason !== "기타"} value={enrollmentForm.endReasonNote} onChange={(event) => setEnrollmentForm((current) => ({ ...current, endReasonNote: event.target.value }))} placeholder="직접 입력" /></div></Space></Radio.Group></Form.Item></td></tr>}
                  <tr><th scope="row">할인 또는 메모</th><td colSpan={3}><Form.Item className="course-table-form-item"><Input.TextArea value={enrollmentForm.memo} onChange={(event) => setEnrollmentForm((current) => ({ ...current, memo: event.target.value }))} rows={3} /></Form.Item></td></tr>
                </tbody>
              </table>
            </div>
          </Form>
        </AntModal>

        <AntModal
          title="수강 이력 상세"
          className="student-management-modal student-enrollment-detail-modal"
          open={Boolean(viewingEnrollment)}
          onCancel={() => closeRecordPanel("enrollment")}
          footer={(
            <StudentActions danger={manager && !viewingEnrollment?.pending && viewingEnrollment?.status !== "종료" ? <Button danger onClick={() => endEnrollment(viewingEnrollment)}>수강 종료</Button> : null}>
                <Button onClick={() => closeRecordPanel("enrollment")}>닫기</Button>
                {manager && viewingEnrollment?.status !== "종료" && (
                  <Button type="primary" onClick={() => {
                    const enrollment = viewingEnrollment;
                    setViewingEnrollment(null);
                    openEnrollmentEdit(enrollment);
                  }}>수정</Button>
                )}
            </StudentActions>
          )}
          width={760}
          destroyOnHidden
        >
          {viewingEnrollment && (
            <div className="course-detail-table-wrap">
              <table className="course-detail-table student-enrollment-detail-table">
                <colgroup><col /><col /><col /><col /></colgroup>
                <tbody>
                  <tr><th scope="row">수업명</th><td colSpan={3}>{viewingEnrollment.course ? getCourseLabel(viewingEnrollment.course, subjects, teachers) : "수업 미지정"}</td></tr>
                  <tr><th scope="row">수업 유형</th><td colSpan={3}>{viewingEnrollment.course?.courseType === "special" ? "특강" : "정규"}</td></tr>
                  <tr><th scope="row">수강 기간</th><td>{viewingEnrollment.pending ? "-" : `${dayjs(viewingEnrollment.startDate).format("YYYY.MM.DD")} ~ ${viewingEnrollment.endDate ? dayjs(viewingEnrollment.endDate).format("YYYY.MM.DD") : "수강 중"}`}</td><th scope="row">상태</th><td>{renderEnrollmentStatusTag(viewingEnrollment.status)}</td></tr>
                  <tr><th scope="row">월 정상 수강료</th><td>{manager ? formatWon(viewingEnrollment.appliedTuition) : "-"}</td><th scope="row">첫 달 조정</th><td>{!manager || viewingEnrollment.firstMonthAdjustment === null ? "-" : formatWon(viewingEnrollment.firstMonthAdjustment)}</td></tr>
                  <tr><th scope="row">수강 종료 사유</th><td colSpan={3}>{viewingEnrollment.endReason ? viewingEnrollment.endReason === "기타" ? `기타 - ${viewingEnrollment.endReasonNote || "-"}` : viewingEnrollment.endReason : "-"}</td></tr>
                  <tr><th scope="row">메모</th><td colSpan={3}>{viewingEnrollment.memo || viewingEnrollment.discountMemo || "-"}</td></tr>
                </tbody>
              </table>
            </div>
          )}
        </AntModal>

        <CourseSetupModal open={Boolean(pendingSetup)} initial={pendingSetup}
          title={getCourseLabel(courses.find(course => course.id === pendingSetup?.courseGroupId) || {}, subjects, teachers)}
          saving={store.saving} onClose={() => setPendingSetup(null)}
          onSave={async setup => { if (await store.confirmPendingEnrollment(pendingSetup.id, setup)) setPendingSetup(null); }} />
        <Card className="academy-management-card student-detail-section-card student-record-tabs-card">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: "chart",
                label: "성적 추이",
                children: (
                  <div className="academy-tab-panel">
                    <section aria-label="성적 추이 그래프">
                      <Suspense fallback={<ContentLoading label="성적 그래프를 불러오는 중입니다." />}><ScoreChart chart={scoreChart} loading={scoreChartLoading} error={scoreChartError} onRetry={reloadScoreChart} /></Suspense>
                    </section>
                    <section className="academy-tab-panel" aria-label="성적 히스토리">
                      <div className="section-toolbar">
                        <h3>성적 히스토리</h3>
                        <Button type="primary" icon={<Plus size={16} />} onClick={openScoreModal}>성적 추가</Button>
                      </div>
                      <ScoreTable scores={scores} onView={score => routeStudentId ? navigation.openPanel("score", score.id) : setSelectedScore(score)} onRemove={removeScore} />
                    </section>
                  </div>
                )
              },
              {
                key: "consults",
                label: "상담 일지",
                children: (
                  <div className="academy-tab-panel">
                    <div className="section-toolbar">
                      <h3>상담 일지</h3>
                      <Button type="primary" icon={<Plus size={16} />} onClick={openConsultationCreate}>상담 추가</Button>
                    </div>
                    <ConsultationTable consultations={consultations} onView={openConsultationDetail} onRemove={removeConsultation} />
                  </div>
                )
              }
            ]}
          />
        </Card>

        {scoreModalOpen && (
          <AntModal
            title="성적 추가"
            open={scoreModalOpen}
            onCancel={() => setScoreModalOpen(false)}
            width={640}
            className="student-management-modal student-record-modal"
            footer={[
              <Button key="cancel" onClick={() => setScoreModalOpen(false)}>취소</Button>,
              <Button key="save" type="primary" htmlType="submit" form="student-score-form">성적 저장</Button>
            ]}
            destroyOnHidden
          >
            <ScoreEditor formId="student-score-form" scoreForm={scoreForm} setScoreForm={setScoreForm} onSubmit={saveScore} fileList={scoreFileList} setFileList={setScoreFileList} />
          </AntModal>
        )}

        {selectedScore && (
          <AntModal title="성적 정보" open={Boolean(selectedScore)} onCancel={() => closeRecordPanel("score")} width={640} className="student-management-modal student-record-modal" footer={<Button onClick={() => closeRecordPanel("score")}>닫기</Button>} destroyOnHidden>
            <div className="detail-list student-record-detail">
              <div><span>시험 이름</span><strong>{selectedScore.title}</strong></div>
              <div><span>과목</span><strong>{selectedScore.subject}</strong></div>
              <div><span>시험일</span><strong>{formatDate(selectedScore.date)}</strong></div>
              <div><span>레벨</span><strong>{selectedScore.level}</strong></div>
              <div><span>점수</span><strong>{selectedScore.score}</strong></div>
              <div><span>만점</span><strong>{selectedScore.maxScore}</strong></div>
              <div className="wide"><span>코멘트</span><p>{selectedScore.comment || "-"}</p></div>
              <div className="wide">
                <span>시험지 스캔본 다운로드</span>
                <div className="download-list">
                  {selectedScore.files?.length ? selectedScore.files.map((file) => <FileDownload key={file.id} file={file} />) : <p className="muted">첨부된 시험지 스캔본이 없습니다.</p>}
                </div>
              </div>
            </div>
          </AntModal>
        )}

        {consultationEditForm && (
          <AntModal
            title="상담 내용"
            open={Boolean(consultationEditForm)}
            onCancel={() => setConsultationEditForm(null)}
            width={720}
            className="student-management-modal student-record-modal"
            footer={(
              <StudentActions danger={<Button danger onClick={() => removeConsultation(consultationEditForm.id)}>삭제</Button>}>
                  <Button onClick={() => setConsultationEditForm(null)}>취소</Button>
                  <Button type="primary" htmlType="submit" form="student-consultation-edit-form">저장</Button>
              </StudentActions>
            )}
            destroyOnHidden
          >
            <Form id="student-consultation-edit-form" className="student-record-form" layout="vertical" size="medium" onFinish={saveConsultationEdit}>
              <Row gutter={[12, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item label="학생 이름">
                    <Input value={selectedStudent.name} disabled />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="상담 일시" required>
                    <Input type="datetime-local" value={consultationEditForm.date} onChange={(event) => setConsultationEditForm({ ...consultationEditForm, date: event.target.value })} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="분류">
                    <Select
                      value={consultationEditForm.type}
                      onChange={(type) => setConsultationEditForm({ ...consultationEditForm, type })}
                      options={["학생상담", "학부모상담"].map((value) => ({ value, label: value }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label="상담 내용" required>
                    <Input.TextArea value={consultationEditForm.content} onChange={(event) => setConsultationEditForm({ ...consultationEditForm, content: event.target.value })} rows={6} />
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label="첨부파일">
                    {consultationEditForm.files?.length ? (
                      <Space direction="vertical" style={{ width: "100%" }}>
                        {consultationEditForm.files.map((file) => (
                          <Flex key={file.id} align="center" justify="space-between" gap="small">
                            <FileDownload file={file} />
                            <Button danger size="small" onClick={() => removeConsultationAttachment(file.id)}>
                              제거
                            </Button>
                          </Flex>
                        ))}
                      </Space>
                    ) : <AppEmpty description="첨부파일이 없습니다." />}
                  </Form.Item>
                </Col>
                <Col xs={24}>
                  <Form.Item label="첨부파일 교체">
                    <Upload multiple beforeUpload={() => false} fileList={consultationEditFileList} onChange={({ fileList }) => setConsultationEditFileList(fileList)} accept=".jpg,.jpeg,.png,.pdf">
                      <Button icon={<FileText size={16} />}>파일 선택</Button>
                    </Upload>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </AntModal>
        )}
      </div>
    );
  }

  return (
    <div className="page-stack student-management-page">
      <Card title="학생 검색" className="academy-management-card">
        <div className="student-filter-form">
          <div className="student-filter-inline">
            <Input className="student-filter-search" value={studentFilterInput.name} onChange={(event) => updateStudentFilter({ name: event.target.value })} prefix={<Search size={15} />} placeholder="학생 이름" />
            <div className={`student-filter-control${studentFilterInput.school ? " is-active" : ""}`}><span>학교:</span><Select value={studentFilterInput.school || undefined} onChange={(school) => updateStudentFilter({ school: school || "" })} options={studentSchoolOptions} placeholder="전체" allowClear showSearch filterOption={matchesSchoolSearch} /></div>
            <div className={`student-filter-control${studentFilterInput.grade ? " is-active" : ""}`}><span>학년:</span><Select value={studentFilterInput.grade || undefined} onChange={(grade) => updateStudentFilter({ grade: grade || "" })} options={studentGradeOptions} placeholder="전체" allowClear /></div>
            <div className={`student-filter-control${studentFilterInput.status ? " is-active" : ""}`}><span>상태:</span><Select value={studentFilterInput.status || undefined} onChange={(status) => updateStudentFilter({ status: status || "" })} options={["", ...studentStatusValues].map((value) => ({ value, label: value || "전체" }))} placeholder="전체" /></div>
            <div className={`student-filter-control${studentFilterInput.courseType !== "all" ? " is-active" : ""}`}><span>유형:</span><Select disabled={!manager} value={studentFilterInput.courseType || "all"} onChange={(courseType) => updateStudentFilter({ courseType })} options={[{ value: "all", label: "전체" }, { value: "regular_only", label: "정규" }, { value: "special_only", label: "특강" }, { value: "regular_and_special", label: "정규+특강" }]} /></div>
            <Button htmlType="button" className="student-filter-reset" onClick={resetStudentFilters} icon={<RotateCcw size={14} />}>필터 초기화</Button>
          </div>
        </div>
      </Card>
      <Card className="academy-management-card">
        <div className="academy-tab-panel">
          <div className="section-toolbar">
            <h3>학생 목록</h3>
            <Space className="section-toolbar-actions" size={8} wrap>
              <Tooltip title="다음 학년도 학생의 학년·진학·졸업 변경을 확인하고 예약합니다.">
                <Button className="student-list-action-button" icon={<GraduationCap size={16} />} disabled title="현재 서버에서 학년 변경 예약을 지원하지 않습니다." onClick={openPromotionPage}>학년 변경</Button>
              </Tooltip>
              <Button className="student-list-action-button" type="primary" icon={<Plus size={16} />} onClick={openCreate}>학생 등록</Button>
            </Space>
          </div>
          <Table
            rowKey="id"
            columns={studentColumns}
            dataSource={students}
            locale={{ emptyText: <AppEmpty description="조건에 맞는 학생이 없습니다." /> }}
            pagination={studentPagination}
            scroll={{ x: 640 }}
            onRow={clickableRow(openDetail)}
          />
        </div>
      </Card>
    </div>
  );
}
